import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Card, CardContent, Button, TextField,
  LinearProgress, Chip, CircularProgress,
} from "@mui/material";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

const D = {
  bg:          "#0a0a0f",
  surface:     "#111118",
  surfaceHigh: "#16161f",
  border:      "#1e1e2e",
  borderMed:   "#2a2a3e",
  accent:      "#7c6aff",
  accentSoft:  "rgba(124,106,255,0.12)",
  accentBorder:"rgba(124,106,255,0.25)",
  text:        "#e8e8f0",
  textSec:     "#a0a0b8",
  textMuted:   "#6b6b8a",
  success:     "#22c55e",
  successSoft: "rgba(34,197,94,0.08)",
  danger:      "#ef4444",
  dangerSoft:  "rgba(239,68,68,0.08)",
  font:        "'Inter', sans-serif",
  fontDisplay: "'Inter', sans-serif",
  fontMono:    "'JetBrains Mono', 'Fira Code', monospace",
};

const FORMATS = [
  {
    value: "reels",
    label: "Reel",
    icon: "🎬",
    desc: "Retention-first · Platform-native · Scroll-stopping · TikTok / Instagram / YouTube Shorts",
    durations: [15, 30, 60, 90],
  },
  {
    value: "ads",
    label: "Ad",
    icon: "📢",
    desc: "Conversion-first · Direct response · Sales-focused · Meta / TikTok / YouTube Pre-roll",
    durations: [15, 30, 60],
  },
];

const STYLES = [
  { value: "realistic",    label: "Realistic / Cinematic",       icon: "🎥" },
  { value: "animated",     label: "Animated / Motion Graphics",  icon: "✨" },
  { value: "talking_head", label: "Talking Head",                icon: "🗣️" },
  { value: "text_broll",   label: "Text + B-Roll",               icon: "📝" },
  { value: "product",      label: "Product Showcase",            icon: "📦" },
];

const STEPS = ["Source", "Format", "Duration", "Style", "Notes", "Submit"];

export default function VideoWizard() {
  const navigate  = useNavigate();
  const [hasAccess, setHasAccess]   = useState(null);
  const [credits, setCredits]       = useState(0);
  const [step, setStep]             = useState(0);

  // Form state
  const [postContent, setPostContent] = useState("");
  const [format, setFormat]           = useState("");
  const [duration, setDuration]       = useState(null);
  const [style, setStyle]             = useState("");
  const [notes, setNotes]             = useState("");

  // Job state
  const [jobId, setJobId]       = useState(null);
  const [status, setStatus]     = useState(null);
  const [pct, setPct]           = useState(0);
  const [stepLabel, setStepLabel] = useState("");
  const [error, setError]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  const token = () => localStorage.getItem("token");
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  useEffect(() => {
    if (!token()) { navigate("/login"); return; }
    axios.get(`${API}/api/auth/me`, { headers: authHeaders() })
      .then(r => {
        if (!r.data?.has_video) { setHasAccess(false); return; }
        setHasAccess(true);
      })
      .catch(() => navigate("/login"));

    axios.get(`${API}/api/dashboard/usage`, { headers: authHeaders() })
      .then(r => setCredits(r.data?.video_credits ?? 0))
      .catch(() => {});
  }, []);

  // Polling
  useEffect(() => {
    if (!jobId) return;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/video/status/${jobId}`, { headers: authHeaders() });
        const d = r.data;
        setStatus(d.status);
        setPct(d.pct || 0);
        setStepLabel(d.step || "");
        if (d.status === "ready" || d.status === "failed") {
          clearInterval(pollRef.current);
          if (d.status === "failed") setError(d.error || "Generation failed. Credit refunded.");
        }
      } catch { clearInterval(pollRef.current); }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [jobId]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const r = await axios.post(`${API}/api/video/submit`, {
        post_content: postContent,
        format, duration, style, notes,
      }, { headers: authHeaders() });
      setJobId(r.data.job_id);
      setCredits(r.data.credits_remaining);
      setStatus("queued");
      setStep(6); // jump to status view
    } catch (e) {
      setError(e.response?.data?.detail || "Submission failed.");
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    try {
      const r = await axios.get(`${API}/api/video/download/${jobId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url  = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `video_package_${format}_${duration}s.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch { setError("Download failed."); }
  };

  const selectedFormat = FORMATS.find(f => f.value === format);
  const canNext = [
    postContent.trim().length >= 10,
    !!format,
    !!duration,
    !!style,
    true,  // notes optional
  ][step] ?? true;

  // ── Access gate ────────────────────────────────────────────────────────────
  if (hasAccess === null) return (
    <Box sx={{ minHeight: "100vh", background: D.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CircularProgress sx={{ color: D.accent }} />
    </Box>
  );

  if (hasAccess === false) {
    navigate("/home", { replace: true });
    return null;
  }

  // ── Processing / result view ───────────────────────────────────────────────
  if (step === 6 || jobId) {
    const done   = status === "ready";
    const failed = status === "failed";
    return (
      <Box sx={{ minHeight: "100vh", background: D.bg, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Card sx={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: "18px", p: 4, maxWidth: 560, width: "100%" }}>
          <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "20px", color: D.text, mb: 3, textAlign: "center" }}>
            {done ? "🎬 Video Package Ready" : failed ? "❌ Generation Failed" : "⏳ Generating Package…"}
          </Typography>

          {!done && !failed && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.fontMono }}>{stepLabel || "Processing…"}</Typography>
                <Typography sx={{ fontSize: "12px", color: D.accent, fontFamily: D.fontMono }}>{pct}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct} sx={{ borderRadius: "4px", background: D.border, "& .MuiLinearProgress-bar": { background: D.accent } }} />
            </Box>
          )}

          {done && (
            <Box sx={{ mb: 3, p: 2.5, background: D.accentSoft, border: `1px solid ${D.accentBorder}`, borderRadius: "10px" }}>
              <Typography sx={{ fontSize: "13.5px", color: D.text, lineHeight: 1.7 }}>
                Your complete video production package is ready — script, visual asset prompts, transcript, and music direction.
                Download the ZIP and use the production guide to create the video.
              </Typography>
            </Box>
          )}

          {failed && (
            <Box sx={{ mb: 3, p: 2.5, background: D.dangerSoft, border: "1px solid rgba(239,68,68,0.25)", borderRadius: "10px" }}>
              <Typography sx={{ fontSize: "13px", color: D.danger }}>{error || "Generation failed. Your credit has been refunded."}</Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {done && (
              <Button
                onClick={handleDownload}
                variant="contained"
                fullWidth
                sx={{ background: D.accent, textTransform: "none", fontWeight: 700, borderRadius: "10px", py: 1.4, fontSize: "14px",
                  "&:hover": { background: "#6b5ce7" } }}
              >
                Download Production Package (ZIP)
              </Button>
            )}
            <Button
              onClick={() => navigate("/my-videos")}
              variant="outlined"
              fullWidth
              sx={{ borderColor: D.border, color: D.textSec, textTransform: "none", borderRadius: "10px",
                "&:hover": { borderColor: D.accent, color: D.accent } }}
            >
              View All My Requests
            </Button>
            <Button onClick={() => navigate("/home")} sx={{ textTransform: "none", color: D.textMuted, fontSize: "13px" }}>
              ← Back to Home
            </Button>
          </Box>
        </Card>
      </Box>
    );
  }

  // ── Wizard steps ───────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", background: D.bg, py: 5, px: 2 }}>
      <Box sx={{ maxWidth: 640, mx: "auto" }}>

        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "22px", color: D.text }}>🎬 Video Request</Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, mt: 0.5 }}>
              Short-form video production brief — AI writes the script and all asset prompts
            </Typography>
          </Box>
          <Chip
            label={`${credits} credit${credits !== 1 ? "s" : ""}`}
            size="small"
            sx={{ background: credits > 0 ? D.accentSoft : D.dangerSoft,
              color: credits > 0 ? D.accent : D.danger,
              border: `1px solid ${credits > 0 ? D.accentBorder : "rgba(239,68,68,0.25)"}`,
              fontFamily: D.fontMono, fontSize: "11px", fontWeight: 700 }}
          />
        </Box>

        {/* Step indicator */}
        <Box sx={{ display: "flex", gap: 0.75, mb: 4 }}>
          {STEPS.map((s, i) => (
            <Box key={i} sx={{ flex: 1, height: 3, borderRadius: "2px",
              background: i <= step ? D.accent : D.border, transition: "background 0.2s" }} />
          ))}
        </Box>
        <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, letterSpacing: "0.09em", textTransform: "uppercase", mb: 2 }}>
          Step {step + 1} of {STEPS.length} — {STEPS[step]}
        </Typography>

        {/* ── Step 0: Source post ── */}
        {step === 0 && (
          <Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.text, mb: 0.75 }}>
              What's the video about?
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, mb: 2.5 }}>
              Paste a post you want turned into a video, or write a topic/brief directly.
            </Typography>
            <TextField
              multiline rows={6}
              fullWidth
              placeholder="Paste your post text here, or describe what you want the video to cover…"
              value={postContent}
              onChange={e => setPostContent(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: D.surfaceHigh, borderRadius: "10px", fontSize: "14px", color: D.text, fontFamily: D.font,
                  "& fieldset": { borderColor: D.border },
                  "&:hover fieldset": { borderColor: D.borderMed },
                  "&.Mui-focused fieldset": { borderColor: D.accent },
                },
              }}
            />
            <Typography sx={{ fontSize: "11px", color: D.textMuted, mt: 1 }}>
              {postContent.length} characters {postContent.trim().length < 10 && postContent.length > 0 ? "— need at least 10" : ""}
            </Typography>
          </Box>
        )}

        {/* ── Step 1: Format ── */}
        {step === 1 && (
          <Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.text, mb: 0.75 }}>
              Choose video format
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, mb: 2.5 }}>
              Two completely different AI agents, two completely different scripts.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {FORMATS.map(f => (
                <Box
                  key={f.value}
                  onClick={() => { setFormat(f.value); setDuration(null); }}
                  sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${format === f.value ? D.accent : D.border}`,
                    background: format === f.value ? D.accentSoft : D.surface, cursor: "pointer", transition: "all 0.15s",
                    "&:hover": { borderColor: D.accent } }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}>
                    <Typography sx={{ fontSize: "22px" }}>{f.icon}</Typography>
                    <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", color: D.text }}>{f.label}</Typography>
                    {format === f.value && <Chip label="Selected" size="small" sx={{ background: D.accent, color: "#fff", fontSize: "10px", height: "18px", ml: "auto" }} />}
                  </Box>
                  <Typography sx={{ fontSize: "12.5px", color: D.textMuted }}>{f.desc}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Step 2: Duration ── */}
        {step === 2 && selectedFormat && (
          <Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.text, mb: 0.75 }}>
              Choose duration
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, mb: 2.5 }}>
              {format === "reels" ? "Reels: 15 / 30 / 60 / 90 seconds" : "Ads: 15 / 30 / 60 seconds"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {selectedFormat.durations.map(d => (
                <Box
                  key={d}
                  onClick={() => setDuration(d)}
                  sx={{ px: 3, py: 2.5, borderRadius: "12px", border: `1px solid ${duration === d ? D.accent : D.border}`,
                    background: duration === d ? D.accentSoft : D.surface, cursor: "pointer", transition: "all 0.15s",
                    minWidth: 90, textAlign: "center", "&:hover": { borderColor: D.accent } }}
                >
                  <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "22px", color: duration === d ? D.accent : D.text }}>{d}s</Typography>
                  <Typography sx={{ fontSize: "11px", color: D.textMuted, mt: 0.5 }}>
                    {d === 15 ? "Ultra-short" : d === 30 ? "Standard" : d === 60 ? "Long" : "Extended"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Step 3: Style ── */}
        {step === 3 && (
          <Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.text, mb: 0.75 }}>
              Choose visual style
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, mb: 2.5 }}>
              The AI will write the script and visual prompts optimized for this style.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {STYLES.map(s => (
                <Box
                  key={s.value}
                  onClick={() => setStyle(s.value)}
                  sx={{ p: 2, borderRadius: "10px", border: `1px solid ${style === s.value ? D.accent : D.border}`,
                    background: style === s.value ? D.accentSoft : D.surface, cursor: "pointer", transition: "all 0.15s",
                    display: "flex", alignItems: "center", gap: 1.5, "&:hover": { borderColor: D.accent } }}
                >
                  <Typography sx={{ fontSize: "20px" }}>{s.icon}</Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: style === s.value ? 600 : 400, color: style === s.value ? D.accent : D.text }}>
                    {s.label}
                  </Typography>
                  {style === s.value && <Chip label="✓" size="small" sx={{ ml: "auto", background: D.accent, color: "#fff", fontSize: "10px", height: "18px", minWidth: "22px" }} />}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Step 4: Notes ── */}
        {step === 4 && (
          <Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.text, mb: 0.75 }}>
              Any specific notes? <Typography component="span" sx={{ fontSize: "13px", color: D.textMuted, fontWeight: 400 }}>(optional)</Typography>
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, mb: 2.5 }}>
              Product focus, brand references, specific people to feature, anything the director should know.
            </Typography>
            <TextField
              multiline rows={5}
              fullWidth
              placeholder="e.g. Use our founder. Focus on the dog food product. Show kitchen setting. Warm and homey feeling…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: D.surfaceHigh, borderRadius: "10px", fontSize: "14px", color: D.text, fontFamily: D.font,
                  "& fieldset": { borderColor: D.border },
                  "&:hover fieldset": { borderColor: D.borderMed },
                  "&.Mui-focused fieldset": { borderColor: D.accent },
                },
              }}
            />
          </Box>
        )}

        {/* ── Step 5: Review & Submit ── */}
        {step === 5 && (
          <Box>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.text, mb: 2.5 }}>
              Review & Submit
            </Typography>

            <Card sx={{ background: D.surfaceHigh, border: `1px solid ${D.border}`, borderRadius: "12px", p: 3, mb: 3 }}>
              {[
                ["Format",   format === "reels" ? "🎬 Reel" : "📢 Ad"],
                ["Duration", `${duration} seconds`],
                ["Style",    STYLES.find(s => s.value === style)?.label],
                ["Notes",    notes || "None"],
              ].map(([label, value]) => (
                <Box key={label} sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                  <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.fontMono, minWidth: 70, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
                  <Typography sx={{ fontSize: "13.5px", color: D.text }}>{value}</Typography>
                </Box>
              ))}
              <Box sx={{ pt: 1.5, borderTop: `1px solid ${D.border}`, mt: 1 }}>
                <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.fontMono, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>Source</Typography>
                <Typography sx={{ fontSize: "13px", color: D.textSec, fontStyle: "italic" }}>
                  "{postContent.slice(0, 100)}{postContent.length > 100 ? "…" : ""}"
                </Typography>
              </Box>
            </Card>

            <Box sx={{ p: 2, background: D.accentSoft, border: `1px solid ${D.accentBorder}`, borderRadius: "10px", mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: "13.5px", color: D.text }}>Cost: <strong>1 video credit</strong></Typography>
              <Typography sx={{ fontSize: "13.5px", color: D.textMuted }}>Balance after: <strong style={{ color: credits > 1 ? D.accent : D.danger }}>{credits - 1} credits</strong></Typography>
            </Box>

            {credits <= 0 && (
              <Box sx={{ p: 2, background: D.dangerSoft, border: "1px solid rgba(239,68,68,0.25)", borderRadius: "10px", mb: 2 }}>
                <Typography sx={{ fontSize: "13px", color: D.danger }}>No credits remaining. Contact support to add video credits.</Typography>
              </Box>
            )}

            {error && (
              <Box sx={{ p: 2, background: D.dangerSoft, border: "1px solid rgba(239,68,68,0.25)", borderRadius: "10px", mb: 2 }}>
                <Typography sx={{ fontSize: "13px", color: D.danger }}>{error}</Typography>
              </Box>
            )}

            <Button
              onClick={handleSubmit}
              disabled={submitting || credits <= 0}
              variant="contained"
              fullWidth
              sx={{ background: D.accent, textTransform: "none", fontWeight: 700, borderRadius: "12px", py: 1.6, fontSize: "15px",
                "&:hover": { background: "#6b5ce7" }, "&.Mui-disabled": { background: D.border, color: D.textMuted } }}
            >
              {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Submit Video Request (1 credit)"}
            </Button>
          </Box>
        )}

        {/* Navigation */}
        {step < 6 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              onClick={() => step === 0 ? navigate("/home") : setStep(s => s - 1)}
              sx={{ textTransform: "none", color: D.textMuted, fontSize: "13px" }}
            >
              {step === 0 ? "← Home" : "← Back"}
            </Button>
            {step < 5 && (
              <Button
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext}
                variant="contained"
                sx={{ background: D.accent, textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 3,
                  "&.Mui-disabled": { background: D.border, color: D.textMuted } }}
              >
                Next →
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
